/** Hierarchical pose for tagged, merged geometry; position and normals move together. */
export const WARRIOR_POSE = /* glsl */ `
  uniform float uTime;
  void pivotX(inout vec3 p, inout vec3 n, float a, vec3 pivot) {
    float c = cos(a), s = sin(a); mat2 r = mat2(c, s, -s, c);
    p -= pivot; p.yz = r * p.yz; p += pivot; n.yz = r * n.yz;
  }
  void pivotY(inout vec3 p, inout vec3 n, float a, vec3 pivot) {
    float c = cos(a), s = sin(a); mat2 r = mat2(c, -s, s, c);
    p -= pivot; p.xz = r * p.xz; p += pivot; n.xz = r * n.xz;
  }
  void pivotZ(inout vec3 p, inout vec3 n, float a, vec3 pivot) {
    float c = cos(a), s = sin(a); mat2 r = mat2(c, s, -s, c);
    p -= pivot; p.xy = r * p.xy; p += pivot; n.xy = r * n.xy;
  }
  void warriorPose(inout vec3 p, inout vec3 n) {
    // frozen enemies keep the stride they were caught in (gait is encoded as -1 - gait)
    float part = abs(aPart), side = sign(aPart), run = iFx.z, drive = iFx.w < -3.5 ? -iFx.w - 4.0 : iFx.w < -0.5 ? -iFx.w - 1.0 : max(0.0, iFx.w), heavy = iCrowd.z;
    float seed = iPersona.x, amp = iPersona.y, aliveMotion = step(-0.5,iFx.w);
    float breath = sin(uTime * (1.7 + seed * 1.4) + seed * 47.0) * aliveMotion;
    float phase = iAnim.x, cycle = phase + (side < 0.0 ? 3.141593 : 0.0) + side * (seed - 0.5) * 0.22;
    float pace = sin(cycle), recovery = max(0.0, -pace);
    float attack = iPersona.w, beat = iPersona.z;
    float wind = smoothstep(0.08,0.43,beat) * (1.0-smoothstep(0.43,0.62,beat));
    float strike = smoothstep(0.43,0.58,beat) * (1.0-smoothstep(0.62,0.95,beat));
    // Element-specific flinch (hit 0..1 decays in ~0.15 s) and status postures.
    // arcane hits (4) recoil and fall like fire; their colour is handled by the material
    float hit = iAnim.y, hk = iCrowd.w > 5.5 && iCrowd.w < 6.5 ? 3.0 : iCrowd.w > 3.5 ? 0.0 : iCrowd.w;
    float kr = hit * (1.0 - step(0.5, hk)), kb = hit * step(0.5, hk) * (1.0 - step(1.5, hk));
    float ky = hit * step(1.5, hk) * (1.0 - step(2.5, hk)), kg = hit * step(2.5, hk);
    float slowed = iAnim.w * aliveMotion, poisoned = iFx.x * aliveMotion;
    float twitch = sin(uTime * 71.0 + seed * 13.0 + part * 2.3) * ky;
    // impatience: standing in a jam (no gait, not striking) they shift feet, sway, look around, tap weapons
    float idle = (1.0 - min(1.0, drive * 1.6)) * aliveMotion * (1.0 - attack) * (1.0 - step(0.001, iAnim.z));
    float fidget = uTime * (2.1 + seed * 0.8) + seed * 17.0;
    if (part >= 1.0 && part <= 3.0) {
      float hip = pace * (0.36 + run * 0.28) * drive * amp * (1.0 - heavy * 0.2);
      float knee = recovery * recovery * (0.85 + run * 0.45) * drive * (1.0 - heavy * 0.15);
      float x = sign(p.x) * mix(0.1, 0.15, heavy);
      if (part > 2.5) pivotX(p, n, -hip * 0.55 - knee * 0.4, vec3(x, 0.07, 0.045));
      if (part > 1.5) pivotX(p, n, knee, vec3(x, 0.28, 0.02));
      pivotX(p, n, hip, vec3(x, 0.56, 0.0));
      // shifting from foot to foot in place
      float shuffle = max(0.0, sin(fidget + side * 1.571)) * idle;
      if (part > 1.5) pivotX(p, n, shuffle * 0.22, vec3(x, 0.28, 0.02));
      p.y += recovery * (0.025 + run * 0.035) * drive + shuffle * 0.03;
      p.z += side * attack * 0.055;
    } else {
      if (part == 4.0 || part == 5.0) {
        if (part == 5.0) {
          float elbow = -(0.16 + seed * 0.2 + run * (0.72 + sin(cycle + 0.4) * 0.15)) * (0.35 + drive * 0.65) * aliveMotion;
          elbow += attack * (-wind * 0.8 + strike * 0.5);
          pivotX(p, n, elbow, vec3(-side * mix(mix(0.33, 0.25, run), 0.4, heavy), mix(mix(0.76, 0.79, run), 0.8, heavy), 0.02));
        }
        float swing = -sin(cycle - 0.28 - seed * 0.2) * (0.22 + run * 0.42) * drive * amp;
        swing += attack * (side < 0.0 || heavy > 0.5 ? wind * 1.1 - strike * 1.5 : -0.25 + strike * 0.4);
        pivotX(p, n, swing, vec3(-side * mix(mix(0.28, 0.24, run), 0.36, heavy), mix(1.02, 1.12, heavy), 0.0));
        pivotZ(p,n,(seed-0.5)*0.18 + attack * side * wind * 0.16,vec3(-side*.28,1.02,0.0));
        // fire: forearms up to shield the face; frost: arms hugged in; shock: arms jerk out; venom: hands to the belly
        vec3 shoulder = vec3(-side * 0.3, 1.02, 0.0);
        pivotX(p, n, -kr * 1.1 - kg * 0.55 - poisoned * (side < 0.0 ? 0.55 : 0.0), shoulder);
        pivotZ(p, n, side * (kb * 0.3 + slowed * 0.16) - side * twitch * 0.5, shoulder);
        if (part == 5.0) pivotX(p, n, -kr * 0.6 - poisoned * (side < 0.0 ? 0.85 : 0.0), vec3(-side * 0.33, 0.77, 0.02));
        // the weapon hand taps restlessly while waiting
        if (side > 0.0) pivotX(p, n, -max(0.0, sin(fidget * 1.7)) * 0.16 * idle, shoulder);
      }
      if (part == 6.0) {
        float tail = 1.0 - smoothstep(0.4, 1.15, p.y);
        float flutter = sin(uTime * (2.5+seed*1.4) + p.x * 8.0 + seed * 21.0 + phase * 0.3) * 0.045 * aliveMotion;
        p.z += tail * flutter - tail * tail * (0.04 + run * 0.12);
        n.y += n.z * tail * (0.12 + run * 0.2);
      }
      float twist = sin(phase) * 0.075 * drive * amp + attack * (-wind * 0.18 + strike * 0.2);
      if(part == 7.0) twist += sin(uTime*(0.43+seed*.3)+seed*39.0)*(0.15 + 0.3 * idle)*aliveMotion;
      // a fallen neighbour draws the head (and a little of the shoulders) toward it
      float glance = iGlow.w;
      pivotY(p, n, (part == 7.0 ? twist * -0.3 + glance * 0.85 : twist) + glance * 0.22, vec3(0.0, 0.85, 0.0));
      // recoil from fire, hunch from frost/slow, double over from venom, spasm from shock
      float flinch = -kr * 0.24 + kb * 0.1 + kg * 0.32 + slowed * 0.14 + poisoned * 0.1 + twitch * 0.12;
      pivotX(p, n, (0.035 + run * 0.15) * drive + (seed-0.5)*0.13 + attack*(-wind*.12+strike*.24) + flinch + iCrowd.y, vec3(0.0, 0.56, 0.0));
      float stagger = sin(phase * 0.5 + seed * 6.0) * 0.08 * poisoned + sin(fidget * 0.5) * 0.05 * idle;
      pivotZ(p, n, iCrowd.x + stagger + twitch * 0.1 + sin(phase) * (0.025 + heavy * 0.04) * drive * amp, vec3(0.0, 0.56, 0.0));
      // striking the barrier: the whole upper body lunges into each blow
      p.z += attack * strike * 0.08;
      // cold shiver while slowed or just hit by frost
      p.x += sin(uTime * 55.0 + seed * 9.0) * 0.006 * (slowed + kb);
      p.x += sin(phase) * 0.014 * drive;
      p.y += breath * 0.008;
    }
    p.y += (1.0 - cos(phase * 2.0)) * (0.012 + run * 0.012) * drive;
    if (part == 3.0) p.y = max(p.y, 0.012);
    float dying = iAnim.z;
    if (dying > 0.0) {
      // Death follows the killing element (hitKind is kept on the corpse):
      // fire staggers back flailing, frost falls rigid like a statue, shock convulses with arms flung wide,
      // venom crumples at the knees and pitches forward.
      float burn = 1.0 - step(0.5, hk), rigid = step(0.5, hk) * (1.0 - step(1.5, hk));
      float shock = step(1.5, hk) * (1.0 - step(2.5, hk)), melt = step(2.5, hk);
      float give = smoothstep(0.0, 0.3, dying) * (1.0 - rigid);
      float fall = smoothstep(0.04, mix(0.5, 0.36, rigid), dying);
      fall = fall * fall * (1.0 + (1.0 - fall) * 0.6);
      if (part == 4.0 || part == 5.0) {
        vec3 shoulder = vec3(-side * 0.3, 1.02, 0.0);
        float fling = -give * (1.1 + seed * 0.6) * (1.0 - melt * 0.7);
        fling += burn * sin(uTime * 16.0 + side * 2.0 + seed * 5.0) * 0.8 * give * (1.0 - fall);
        pivotX(p, n, fling + melt * give * 0.5, shoulder);
        pivotZ(p, n, -side * shock * give * 1.2, shoulder);
      }
      p.y -= give * (0.1 + melt * 0.28) * smoothstep(0.0, 0.6, p.y);
      float lean = (seed - 0.5) * (1.1 + rigid * 0.8);
      float spasm = shock * sin(uTime * 70.0 + seed * 20.0 + part) * 0.14 * (1.0 - fall);
      pivotZ(p, n, fall * lean + spasm, vec3(0.0, 0.0, 0.0));
      // venom pitches forward onto the face; everything else goes over backwards
      pivotX(p, n, mix(-fall * (1.3 - heavy * 0.25), fall * 1.15, melt), vec3(0.0, 0.0, mix(-0.12, 0.14, melt)));
    }
    n = normalize(n);
  }
`;
